import { useRef, useState } from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import type { ImageUploadBody } from '@hewey/shared'
import { useOnline } from '@hooks/useOnline.ts'
import { downscaleImage } from '@utils/downscaleImage.ts'
import styles from './ImageUploadControl.module.css'

type Props = {
	hasImage: boolean
	onUpload: (body: ImageUploadBody) => Promise<void>
	onRemove: () => Promise<void>
	// Lifts a local object-URL to the parent so the hero shows the picked photo
	// instantly, before the upload round-trips. Called with null to clear it.
	onPreview: (url: string | null) => void
}

// Reusable camera-roll photo control, overlaid on the recipe/activity hero.
// Owns the hidden file input, the on-device downscale, busy/error state, and
// the optimistic preview. No `capture` attribute so iOS offers camera *or*
// library rather than forcing the camera.
export const ImageUploadControl = ({
	hasImage,
	onUpload,
	onRemove,
	onPreview,
}: Props) => {
	const online = useOnline()
	const inputRef = useRef<HTMLInputElement>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		setError(null)
		const previewUrl = URL.createObjectURL(file)
		onPreview(previewUrl)
		setBusy(true)
		try {
			await onUpload(await downscaleImage(file))
		} catch {
			setError('Upload failed')
		} finally {
			URL.revokeObjectURL(previewUrl)
			onPreview(null)
			setBusy(false)
			if (inputRef.current) inputRef.current.value = ''
		}
	}

	const onRemoveClick = async () => {
		setError(null)
		setBusy(true)
		try {
			await onRemove()
		} catch {
			setError('Remove failed')
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className={styles.root}>
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				className={styles.input}
				onChange={(e) => void onPick(e)}
			/>
			<div className={styles.buttons}>
				<button
					type="button"
					className={styles.button}
					disabled={busy || !online}
					onClick={() => inputRef.current?.click()}
				>
					{busy ? (
						<Loader2
							size={15}
							className={styles.spin}
							aria-hidden="true"
						/>
					) : (
						<Camera size={15} aria-hidden="true" />
					)}
					{hasImage ? 'Replace' : 'Add photo'}
				</button>
				{hasImage ? (
					<button
						type="button"
						className={`${styles.button} ${styles.remove}`}
						disabled={busy || !online}
						onClick={() => void onRemoveClick()}
						aria-label="Remove photo"
					>
						<Trash2 size={15} aria-hidden="true" />
					</button>
				) : null}
			</div>
			{error ? (
				<span className={styles.error} aria-live="polite">
					{error}
				</span>
			) : null}
		</div>
	)
}
